import React, { useState } from 'react'
import { Row, Col, Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { uploadKycDocuments, getUser } from '../../redux/slices/usersSlice'
import { ButtonComponent as Button } from '../../components'

const { Dragger } = Upload

const KycUpload = ({ clientId }) => {
    const dispatch = useDispatch()
    const { isSubmit } = useSelector(state => state.users)
    
    const [documentoFile, setDocumentoFile] = useState(null)
    const [selfieFile, setSelfieFile] = useState(null)
    const [comprovanteFile, setComprovanteFile] = useState(null)
    
    const [documentoFileList, setDocumentoFileList] = useState([])
    const [selfieFileList, setSelfieFileList] = useState([])
    const [comprovanteFileList, setComprovanteFileList] = useState([])

    const validateFile = (file) => {
        const isValidType = file.type === 'application/pdf' || 
                           file.type.startsWith('image/')
        
        if (!isValidType) {
            message.error('Você só pode fazer upload de arquivos PDF ou imagens!')
            return false
        }
        
        const isLessThan10M = file.size / 1024 / 1024 < 10
        if (!isLessThan10M) {
            message.error('O arquivo deve ser menor que 10MB!')
            return false
        }
        
        return true
    }

    const handleDocumentoChange = (info) => {
        let newFileList = [...info.fileList]
        
        // Limitar a 1 arquivo
        newFileList = newFileList.slice(-1)
        
        if (newFileList.length > 0) {
            const file = newFileList[0].originFileObj || newFileList[0]
            if (file && validateFile(file)) {
                // Atualizar fileList para mostrar o arquivo
                newFileList[0].status = 'done'
                setDocumentoFileList(newFileList)
                setDocumentoFile(file)
            } else {
                setDocumentoFileList([])
            }
        } else {
            setDocumentoFileList([])
            setDocumentoFile(null)
        }
    }

    const handleSelfieChange = (info) => {
        let newFileList = [...info.fileList]
        
        // Limitar a 1 arquivo
        newFileList = newFileList.slice(-1)
        
        if (newFileList.length > 0) {
            const file = newFileList[0].originFileObj || newFileList[0]
            if (file && validateFile(file)) {
                // Atualizar fileList para mostrar o arquivo
                newFileList[0].status = 'done'
                setSelfieFileList(newFileList)
                setSelfieFile(file)
            } else {
                setSelfieFileList([])
            }
        } else {
            setSelfieFileList([])
            setSelfieFile(null)
        }
    }

    const handleComprovanteChange = (info) => {
        let newFileList = [...info.fileList]
        
        // Limitar a 1 arquivo
        newFileList = newFileList.slice(-1)
        
        if (newFileList.length > 0) {
            const file = newFileList[0].originFileObj || newFileList[0]
            if (file && validateFile(file)) {
                // Atualizar fileList para mostrar o arquivo
                newFileList[0].status = 'done'
                setComprovanteFileList(newFileList)
                setComprovanteFile(file)
            } else {
                setComprovanteFileList([])
            }
        } else {
            setComprovanteFileList([])
            setComprovanteFile(null)
        }
    }

    const handleSubmit = async () => {
        if (!documentoFile && !selfieFile && !comprovanteFile) {
            message.error('Por favor, selecione pelo menos um documento para fazer upload')
            return
        }

        try {
            await dispatch(uploadKycDocuments({
                idCliente: clientId,
                documento: documentoFile,
                selfie: selfieFile,
                comprovanteEndereco: comprovanteFile
            })).unwrap()
            
            message.success('Documentos KYC enviados com sucesso!')
            
            // Limpar arquivos selecionados
            setDocumentoFile(null)
            setSelfieFile(null)
            setComprovanteFile(null)
            setDocumentoFileList([])
            setSelfieFileList([])
            setComprovanteFileList([])
            
            // Recarregar dados do usuário para atualizar a lista de documentos
            dispatch(getUser({ idClient: clientId }))
        } catch (error) {
            message.error(error || 'Erro ao fazer upload dos documentos KYC')
        }
    }

    const documentoProps = {
        name: 'documento',
        multiple: false,
        fileList: documentoFileList,
        beforeUpload: () => false, // Prevent auto upload
        onChange: handleDocumentoChange,
        onRemove: () => {
            if (!isSubmit) {
                setDocumentoFileList([])
                setDocumentoFile(null)
            }
        },
        accept: '.pdf,.jpg,.jpeg,.png',
        disabled: isSubmit,
        showUploadList: {
            showPreviewIcon: false,
            showRemoveIcon: !isSubmit
        }
    }

    const selfieProps = {
        name: 'selfie',
        multiple: false,
        fileList: selfieFileList,
        beforeUpload: () => false,
        onChange: handleSelfieChange,
        onRemove: () => {
            if (!isSubmit) {
                setSelfieFileList([])
                setSelfieFile(null)
            }
        },
        accept: '.pdf,.jpg,.jpeg,.png',
        disabled: isSubmit,
        showUploadList: {
            showPreviewIcon: false,
            showRemoveIcon: !isSubmit
        }
    }

    const comprovanteProps = {
        name: 'comprovanteEndereco',
        multiple: false,
        fileList: comprovanteFileList,
        beforeUpload: () => false,
        onChange: handleComprovanteChange,
        onRemove: () => {
            if (!isSubmit) {
                setComprovanteFileList([])
                setComprovanteFile(null)
            }
        },
        accept: '.pdf,.jpg,.jpeg,.png',
        disabled: isSubmit,
        showUploadList: {
            showPreviewIcon: false,
            showRemoveIcon: !isSubmit
        }
    }

    return (
        <Row className="mt-25" id="upload-kyc">
            <Col md={4}>
                <label className="tx-placeholder font-bold">Upload KYC</label>
            </Col>
            <Col md={20} className="d-flex flex-column">
                <hr className="profile-divisor" />
                <div className="mt-25 pl-20 pr-20">
                    <Row gutter={[16, 16]}>
                        <Col md={8} style={{ marginBottom: '60px' }}>
                            <label className="tx-pink font-bold">Documento de Identidade</label>
                            <div className="mt-10">
                                <Dragger {...documentoProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
                                    <p className="ant-upload-hint">
                                        Suporta PDF, JPG, PNG (máx. 10MB)
                                    </p>
                                </Dragger>
                            </div>
                        </Col>
                        <Col md={8} style={{ marginBottom: '60px' }}>
                            <label className="tx-pink font-bold">Selfie com Documento</label>
                            <div className="mt-10">
                                <Dragger {...selfieProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
                                    <p className="ant-upload-hint">
                                        Suporta PDF, JPG, PNG (máx. 10MB)
                                    </p>
                                </Dragger>
                            </div>
                        </Col>
                        <Col md={8} style={{ marginBottom: '60px' }}>
                            <label className="tx-pink font-bold">Comprovante de Endereço</label>
                            <div className="mt-10">
                                <Dragger {...comprovanteProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
                                    <p className="ant-upload-hint">
                                        Suporta PDF, JPG, PNG (máx. 10MB)
                                    </p>
                                </Dragger>
                            </div>
                        </Col>
                    </Row>
                    <div style={{ marginTop: '20px' }}>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={isSubmit}
                            disabled={(!documentoFile && !selfieFile && !comprovanteFile) || isSubmit}
                        >
                            {isSubmit ? 'Enviando Documentos...' : 'Enviar Documentos'}
                        </Button>
                    </div>
                </div>
            </Col>
        </Row>
    )
}

export default KycUpload
